import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLatestMacroRegime, useMacroRegimeHistory } from '@/hooks/useMacroRegime';
import {
  ComposedChart, Line, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { ArrowLeft } from 'lucide-react';

type Variant = 'V1' | 'V2' | 'V3' | 'V4';

function useLiquiditySeries() {
  return useQuery({
    queryKey: ['liquidity-series-full'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('liquidity_series_cache')
        .select('week_ending, glp_variants, btc_close_usd, net_liq_yoy_pct')
        .order('week_ending', { ascending: true });
      if (error) throw error;
      return (data || []) as Array<{
        week_ending: string;
        glp_variants: Record<string, number | null> | null;
        btc_close_usd: number | null;
        net_liq_yoy_pct: number | null;
      }>;
    },
    staleTime: 30 * 60 * 1000,
  });
}

function regimeColour(r: string | null | undefined): string {
  if (r === 'expansion') return 'hsl(var(--primary) / 0.08)';
  if (r === 'contraction') return 'hsl(45 100% 50% / 0.10)';
  return 'transparent';
}

export default function Macro() {
  const { data: latest } = useLatestMacroRegime();
  const { data: history = [] } = useMacroRegimeHistory(500);
  const { data: series = [] } = useLiquiditySeries();
  const [variant, setVariant] = useState<Variant>('V2');

  // Chart data: BTC + selected variant + classified regime bands
  const chartData = useMemo(() => {
    // Classify each week's regime band from net_liq_yoy_pct
    return series.map((r) => {
      const variantValue = r.glp_variants?.[variant] ?? null;
      let band: 'expansion' | 'contraction' | 'transition' = 'transition';
      const yoy = r.net_liq_yoy_pct;
      if (yoy !== null && yoy !== undefined) {
        if (yoy > 2) band = 'expansion';
        else if (yoy < -2) band = 'contraction';
      }
      return {
        week: r.week_ending,
        btc: r.btc_close_usd,
        variantValue,
        band,
      };
    });
  }, [series, variant]);

  // Regime band runs for ReferenceAreas
  const bands = useMemo(() => {
    const out: Array<{ start: string; end: string; regime: string }> = [];
    let curStart: string | null = null;
    let curRegime: string | null = null;
    chartData.forEach((d, i) => {
      if (curRegime === null) { curRegime = d.band; curStart = d.week; }
      else if (d.band !== curRegime) {
        out.push({ start: curStart!, end: chartData[i - 1].week, regime: curRegime });
        curRegime = d.band; curStart = d.week;
      }
    });
    if (curStart && curRegime) out.push({ start: curStart, end: chartData[chartData.length - 1]?.week ?? curStart, regime: curRegime });
    return out;
  }, [chartData]);

  // Transition history with subsequent 12w BTC move
  const transitions = useMemo(() => {
    const sorted = [...history].sort((a, b) => a.week_ending.localeCompare(b.week_ending));
    const seriesMap = new Map(series.map((s) => [s.week_ending, s.btc_close_usd]));
    const seriesWeeks = series.map((s) => s.week_ending);
    return sorted
      .filter((r) => r.is_transition)
      .map((r) => {
        const idx = seriesWeeks.indexOf(r.week_ending);
        const fwdIdx = idx >= 0 ? idx + 12 : -1;
        const fwdWeek = fwdIdx >= 0 ? seriesWeeks[fwdIdx] : null;
        const btcNow = r.btc_close_usd ?? seriesMap.get(r.week_ending) ?? null;
        const btcFwd = fwdWeek ? seriesMap.get(fwdWeek) ?? null : null;
        let pct: number | null = null;
        if (btcNow && btcFwd) pct = ((btcFwd - btcNow) / btcNow) * 100;
        return {
          week: r.week_ending,
          from: r.previous_regime ?? '—',
          to: r.regime,
          btc: btcNow,
          fwdPct: pct,
        };
      })
      .reverse();
  }, [history, series]);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <Link to="/"><Button variant="ghost" size="sm" className="mb-4 gap-2"><ArrowLeft className="w-4 h-4" /> Home</Button></Link>

        <h1 className="text-3xl font-bold mb-1">Macro Liquidity Regime</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Top-down context layer. Does not change TOSS scores. Context only — not financial advice.
        </p>

        {latest && (
          <Card className="mb-6">
            <CardContent className="p-5">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Badge className="font-bold">{latest.regime.toUpperCase()}</Badge>
                <span className="text-sm text-muted-foreground">{latest.signal_strength} signal</span>
                <span className="text-sm text-muted-foreground">·</span>
                <span className="text-sm">Net liquidity YoY {(latest.net_liq_yoy_pct ?? 0).toFixed(2)}%</span>
                <span className="text-sm text-muted-foreground">·</span>
                <span className="text-sm">Rolling r {(latest.rolling_r ?? 0).toFixed(2)}</span>
              </div>
              <p className="text-sm leading-relaxed">{latest.observation_note}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Week ending {latest.week_ending} · lead ≈ {latest.lead_estimate_weeks} weeks
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle className="text-lg">BTC vs net liquidity (regime bands)</CardTitle>
            <Select value={variant} onValueChange={(v) => setVariant(v as Variant)}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="V1">V1 — Net liquidity</SelectItem>
                <SelectItem value="V2">V2 — Net / DXY (default)</SelectItem>
                <SelectItem value="V3">V3 — Net + ECB (USD)</SelectItem>
                <SelectItem value="V4">V4 — (Net + ECB) / DXY</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <div className="h-[420px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} minTickGap={40} />
                  <YAxis yAxisId="btc" scale="log" domain={['auto', 'auto']} tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="v" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 12 }}
                  />
                  {bands.map((b, i) => (
                    <ReferenceArea
                      key={i}
                      yAxisId="btc"
                      x1={b.start}
                      x2={b.end}
                      fill={regimeColour(b.regime)}
                      strokeOpacity={0}
                    />
                  ))}
                  <Line yAxisId="btc" type="monotone" dataKey="btc" name="BTC (log USD)" stroke="hsl(var(--foreground))" dot={false} strokeWidth={1.5} />
                  <Line yAxisId="v" type="monotone" dataKey="variantValue" name={`${variant}`} stroke="hsl(var(--primary))" dot={false} strokeWidth={1.5} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-primary/10 border border-primary/30" /> Expansion</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-amber-500/15 border border-amber-500/30" /> Contraction</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-muted border border-border" /> Transition</span>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader><CardTitle className="text-lg">Regime transition history</CardTitle></CardHeader>
          <CardContent>
            {transitions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No transitions recorded yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Week</TableHead>
                    <TableHead>From → To</TableHead>
                    <TableHead className="text-right">BTC at transition</TableHead>
                    <TableHead className="text-right">+12w BTC move</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transitions.map((t) => (
                    <TableRow key={t.week}>
                      <TableCell className="font-mono text-xs">{t.week}</TableCell>
                      <TableCell><span className="text-muted-foreground">{t.from}</span> → <span className="font-medium">{t.to}</span></TableCell>
                      <TableCell className="text-right font-mono text-xs">{t.btc ? `$${Number(t.btc).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}</TableCell>
                      <TableCell className={`text-right font-mono text-xs ${t.fwdPct === null ? '' : t.fwdPct >= 0 ? 'text-emerald-500' : 'text-amber-500'}`}>
                        {t.fwdPct === null ? 'pending' : `${t.fwdPct >= 0 ? '+' : ''}${t.fwdPct.toFixed(1)}%`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">What this layer is (and isn't)</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              <strong className="text-foreground">Net liquidity</strong> is the US Federal Reserve's balance sheet
              (WALCL) minus the Treasury General Account (WTREGEN) and overnight reverse repos (RRPONTSYD).
              It is a coarse proxy for how much USD is sloshing around the financial system. Crypto, as a high-beta
              risk asset, has historically tracked this measure on a multi-month lag.
            </p>
            <p>
              We classify the year-on-year change of net liquidity into <em>expansion</em> (&gt;+2%),
              <em> contraction</em> (&lt;−2%), or <em>transition</em>. The "{variant}" overlay on the chart above is a
              display variant: V2 (net / DXY) adjusts for dollar strength, while V1, V3 and V4 are alternative
              compositions kept for transparency.
            </p>
            <p className="text-foreground">
              <strong>Honest caveat block:</strong> the underlying signal is <em>moderate</em>, not strong. Across the
              validated window the rolling correlation has reversed in roughly a third of periods, including the 2022
              crypto-specific stress where BTC decoupled from liquidity for an extended stretch. Treat this as
              <em> context</em>, never as a timing call.
            </p>
            <p className="text-foreground">
              <strong>This layer does not change TOSS scores.</strong> TOSS is a separate, bottom-up signal that runs
              independently of macro conditions. The macro panel is here to give you a top-down read on the
              environment those picks are operating in — nothing more.
            </p>
            <p>Context only — not financial advice.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
